import { format, isToday, isTomorrow } from 'date-fns';

import type { EffectivePrice } from '@/services/class';
import type {
  TopupContent,
  TopupEligibility,
  TopupSourceType,
} from '@/services/topup';
import type { TopupCode } from '@/state/topup-code';
import type { ClassPackCode } from '@/state/class-pack-code';
import {
  SUBSCRIPTION_CODE_STATUSES,
  type SubscriptionCode,
  type SubscriptionCodeStatus,
} from '@/state/subscription-codes';
import type { Subscription } from '@/state/subscription-groups';
import type { TopupOrderItem } from '@/lib/types';
import {
  formatAmount,
  formatDateWithConditionalYear,
  formatDays,
  formatPrice,
} from '@/lib/utils';

export const getTopupCreditsTotal = (items: TopupOrderItem[] = []): number =>
  items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);

export const formatEffectivePriceLabel = (
  ep: EffectivePrice | undefined | null,
  cashFallback: string
): string | null => {
  if (!ep) return cashFallback;
  if (ep.type === 'unlimited') return null;
  if (ep.type === 'credit') {
    return ep.remaining >= ep.cost
      ? `${ep.cost} credit${ep.cost === 1 ? '' : 's'}`
      : cashFallback;
  }
  return formatPrice(ep.amount, ep.currency);
};

export const pricePillOf = (
  ep: EffectivePrice | undefined,
  cashFallback: string
): string | null => {
  if (ep?.type === 'unlimited') {
    return ep.source === 'subscription'
      ? 'Included in your membership'
      : 'Included in your pack';
  }
  return formatEffectivePriceLabel(ep, cashFallback);
};

export const formatCreditDate = (date: string | null | undefined): string => {
  if (!date) return '-';
  const d = new Date(date);
  if (isToday(d)) return 'Today';
  if (isTomorrow(d)) return 'Tomorrow';
  return format(d, 'MMM d, yyyy');
};

export const getTopupExpiryLabel = (
  expiryPolicy: 'EXPIRE_WITH_PLAN' | 'NEVER',
  planLabel: string,
  creditsExpireAt?: string | null
): string => {
  if (expiryPolicy === 'NEVER') return 'Never expires';
  // Exact date beats plan-name wording: on ROLLOVER plans the credits still
  // die at the period end snapshotted at purchase, so "expires with your
  // plan" would overpromise.
  if (creditsExpireAt) {
    return `Expires ${formatDateWithConditionalYear(creditsExpireAt)}`;
  }
  return `Expires with your ${planLabel}`;
};

export type TopupScope = 'PACK' | 'SUBSCRIPTION' | 'BOTH';

// Neither array linked is an admin config gap; BOTH keeps the generic wording.
export const getTopupScope = (
  topup: Pick<TopupContent, 'topup_class_packs' | 'topup_subscriptions'>
): TopupScope => {
  const hasPacks = (topup.topup_class_packs ?? []).length > 0;
  const hasSubs = (topup.topup_subscriptions ?? []).length > 0;
  if (hasPacks && !hasSubs) return 'PACK';
  if (hasSubs && !hasPacks) return 'SUBSCRIPTION';
  return 'BOTH';
};

export const getTopupStaticPricing = (
  topup: Pick<
    TopupContent,
    'price_for_packs' | 'price_for_subscriptions' | 'same_price_for_all'
  >
): { minPrice: number; allSame: boolean } => {
  const prices = [topup.price_for_packs, topup.price_for_subscriptions].filter(
    (p): p is number => p != null && p > 0
  );
  return {
    minPrice: prices.length ? Math.min(...prices) : 0,
    allSame: topup.same_price_for_all || new Set(prices).size <= 1,
  };
};

// Attach each top-up to its parent plan card; any whose parent isn't shown
// (e.g. a NEVER top-up on a canceled plan) becomes an orphan so a paid
// credit is never hidden.
export const groupTopupsByParent = (
  topupCodes: TopupCode[],
  visiblePackCodeIds: Set<string>,
  visibleSubCodeIds: Set<string>
): {
  topupsByParent: Map<string, TopupCode[]>;
  orphanTopupCodes: TopupCode[];
} => {
  const topupsByParent = new Map<string, TopupCode[]>();
  const orphanTopupCodes: TopupCode[] = [];
  for (const tc of topupCodes) {
    const parentId =
      tc.source_type === 'PACK'
        ? tc.source_class_pack_code?.class_pack_code_id
        : tc.source_subscription_code?.subscription_code_id;
    const inView =
      tc.source_type === 'PACK'
        ? !!parentId && visiblePackCodeIds.has(parentId)
        : !!parentId && visibleSubCodeIds.has(parentId);
    if (parentId && inView) {
      const group = topupsByParent.get(parentId) ?? [];
      group.push(tc);
      topupsByParent.set(parentId, group);
    } else {
      orphanTopupCodes.push(tc);
    }
  }
  return { topupsByParent, orphanTopupCodes };
};

export const pluralizeCredits = (count: number): string =>
  count === 1 ? 'credit' : 'credits';

export const formatClassCount = (count: number): string =>
  `${count} ${count === 1 ? 'class' : 'classes'}`;

// A waitlist hold reserves a credit without spending it: the backend only
// raises credits_held and leaves classes_left at its pre-hold number for the
// whole life of the hold. Every spendable balance and booking gate has to
// subtract it the way the server already does. A backend without waitlists
// sends no credits_held, which holds nothing.
export const getHeldCredits = (code: { credits_held?: number }): number =>
  code.credits_held ?? 0;

export const getAvailableCredits = (code: {
  classes_left: number;
  credits_held?: number;
}): number => code.classes_left - getHeldCredits(code);

export const getAvailableSubCredits = (code: {
  classes_left: number | null;
  credits_held?: number;
}): number | null =>
  code.classes_left === null ? null : code.classes_left - getHeldCredits(code);

// Says why a balance reads lower than the customer expects.
export const heldCreditsLine = (held: number): string | undefined =>
  held > 0 ? `${held} ${pluralizeCredits(held)} held for a waitlist` : undefined;

// `neverExpires` is the one expiry case worth surfacing — dated top-ups
// expire with the plan, so that's left implicit.
export const summarizeTopupCredits = (
  codes: TopupCode[]
): { totalLeft: number; neverExpires: boolean; held: number } => {
  const activeCodes = codes.filter((c) => c.status === 'ACTIVE');
  // Summed before the > 0 filter: a top-up whose credits are all held has
  // nothing left to spend but still explains a lower balance.
  const held = activeCodes.reduce((sum, c) => sum + getHeldCredits(c), 0);
  const active = activeCodes.filter((c) => getAvailableCredits(c) > 0);
  const totalLeft = active.reduce((sum, c) => sum + getAvailableCredits(c), 0);
  const neverExpires =
    active.length > 0 && active.every((c) => c.expires_at === null);

  return { totalLeft, neverExpires, held };
};

// One truthful "Good to know" sentence about top-up expiry. Mirrors
// summarizeTopupCredits' active filter; `rollsOver` is set only for ROLLOVER
// subscriptions, where the plan credits roll but the top-up still dies on its
// snapshotted date — so the copy must say it does NOT roll over.
export const getTopupGoodToKnowLine = (
  codes: TopupCode[],
  rollsOver = false
): string | null => {
  const active = codes.filter(
    (c) => c.status === 'ACTIVE' && getAvailableCredits(c) > 0
  );
  if (active.length === 0) return null;

  const total = active.reduce((sum, c) => sum + getAvailableCredits(c), 0);
  const one = total === 1;
  const subject = one ? 'Your top-up credit' : 'Your top-up credits';
  const verb = one ? 'expires' : 'expire';
  const dated = active.filter((c) => c.expires_at);

  if (dated.length === 0) {
    return `${subject} never ${verb}.`;
  }

  const earliest = dated
    .map((c) => c.expires_at as string)
    .reduce((min, d) => (new Date(d) < new Date(min) ? d : min));
  const when = formatDateWithConditionalYear(earliest);

  if (dated.length < active.length) {
    return `Some top-up credits never expire. Others expire on ${when}.`;
  }
  if (rollsOver) {
    return `${subject} ${one ? "doesn't" : "don't"} roll over and ${verb} on ${when}.`;
  }
  return `${subject} ${verb} on ${when}.`;
};

// Summable balances absorb top-up credits into the count, so the subline is
// what keeps the topped-up portion visible.
export const getBalanceTileParts = (
  baseValue: string,
  baseCount: number | null,
  { totalLeft, held: topupHeld }: { totalLeft: number; held: number },
  held: number
): { value: string; subline?: string } => {
  const subline =
    [
      totalLeft > 0
        ? `+${totalLeft} top-up ${pluralizeCredits(totalLeft)}`
        : undefined,
      heldCreditsLine(held + topupHeld),
    ]
      .filter(Boolean)
      .join(' · ') || undefined;
  if (baseCount !== null) {
    return { value: `${baseCount + totalLeft} left`, subline };
  }
  return { value: baseValue, subline };
};

// Single source of truth for "the offer box will render": the box needs all
// of these fields, so weaker gates upstream would strand its dividers.
export const isTopupOfferReady = (
  eligibility: TopupEligibility | undefined
): eligibility is TopupEligibility & {
  topup: NonNullable<TopupEligibility['topup']>;
  price: number;
  currency: string;
  source_type: TopupSourceType;
} =>
  !!(
    eligibility?.eligible &&
    eligibility.topup &&
    eligibility.source_type &&
    eligibility.price != null &&
    eligibility.currency
  );

export type CreditCardItem =
  | { type: 'pack'; code: ClassPackCode }
  | { type: 'subscription'; code: SubscriptionCode };

// Structural subset satisfied by both the subscription-codes list shape and
// the leaner subscription_code on order responses.
type SubscriptionCreditSource = {
  status: string;
  classes_left: number | null;
  credits_held?: number;
  current_period_end: string | null;
  canceled_at?: string | null;
  subscription?: Pick<Subscription, 'renewal_classes_behavior'> | null;
};

// Backend rejects bookings on any code past current_period_end, so a lapsed
// paused sub's credits are unusable until resumed — and dead entirely if the
// sub is canceled instead.
export const isPausedSubLapsed = (
  sub: SubscriptionCreditSource,
  now: Date = new Date()
): boolean =>
  sub.status === 'PAUSED' &&
  !!sub.current_period_end &&
  new Date(sub.current_period_end) <= now;

// Resuming a RESET plan bills a fresh cycle that overwrites classes_left, so
// a lapsed paused RESET sub's remaining plan credits are already lost.
// ROLLOVER / LEAVE_AS_IS keep them through a resume, so they stay countable.
export const arePausedSubCreditsForfeited = (
  sub: SubscriptionCreditSource,
  now: Date = new Date()
): boolean =>
  isPausedSubLapsed(sub, now) &&
  sub.subscription?.renewal_classes_behavior === 'RESET';

export const getEffectiveClassesLeft = (
  sub: SubscriptionCreditSource,
  now: Date = new Date()
): number | null =>
  arePausedSubCreditsForfeited(sub, now) ? 0 : getAvailableSubCredits(sub);

// Canceling never resumes the period, so a lapsed paused sub's plan credits
// are dead on any renewal behavior, broader than getEffectiveClassesLeft's
// RESET-only forfeit. Lapse is judged on the pre-cancel code because the
// cancel response is no longer PAUSED.
export const getCreditsLeftOnCancel = (
  preCancel: SubscriptionCreditSource,
  balance: SubscriptionCreditSource = preCancel
): number | null =>
  isPausedSubLapsed(preCancel) ? 0 : getAvailableSubCredits(balance);

// A cancelled plan's credits die on its date: the backend books only codes
// whose current_period_end is still ahead, while the row can keep reading
// ACTIVE or FULLY_USED until the backend closes it. PAST_DUE is not listed
// because a paid retry still moves its date forward.
// A missing current_period_end means ended — backend nulls it on force-cancel.
export const isCanceledSubEnded = (
  sub: SubscriptionCreditSource,
  now: Date = new Date()
): boolean => {
  const periodEnded =
    !sub.current_period_end || new Date(sub.current_period_end) <= now;
  const canceled =
    sub.status === 'CANCELED' ||
    (['ACTIVE', 'FULLY_USED', 'PAUSED'].includes(sub.status) &&
      !!sub.canceled_at);
  return canceled && periodEnded;
};

// Mirrors the credit summary card's bucket math for a single code so receipts
// agree with the dashboard.
export const getSpendableClassesLeft = (
  sub: SubscriptionCreditSource,
  now: Date = new Date()
): number | null => {
  if (sub.classes_left === null) return null;
  if (sub.status === 'EXPIRED' || sub.status === 'DELETED') return 0;
  if (isCanceledSubEnded(sub, now)) return 0;
  return getEffectiveClassesLeft(sub, now);
};

// Paused / in-grace-canceled subs should resume (or lapse), not buy more.
export const hasPausedOrCanceledSubscription = (
  cards: CreditCardItem[]
): boolean =>
  cards.some(
    (c) =>
      c.type === 'subscription' &&
      ['PAUSED', 'CANCELED'].includes(c.code.status)
  );

// Tested on the code, not on the card being a subscription: the page also shows
// unknown-status subscriptions now, and those are not in the backend's
// ACTIVE_SUBSCRIPTION_EXISTS set, so treating every subscription card as
// blocking would stop a customer who is free to buy from seeing the option.
export const hasBlockingSubscription = (cards: CreditCardItem[]): boolean =>
  cards.some(
    (c) => c.type === 'subscription' && isBlockingSubscriptionCode(c.code)
  );

// Mirrors the backend's ACTIVE_SUBSCRIPTION_EXISTS set: these codes still
// count as a live subscription, so they block a new purchase and stay
// visible on the credits page.
export const isBlockingSubscriptionCode = (
  c: SubscriptionCode,
  now: Date = new Date()
): boolean =>
  ['ACTIVE', 'PAUSED', 'PAST_DUE', 'FULLY_USED'].includes(c.status) ||
  (c.status === 'CANCELED' &&
    !!c.current_period_end &&
    new Date(c.current_period_end) > now);

export const isKnownSubscriptionCodeStatus = (
  status: string
): status is SubscriptionCodeStatus =>
  (SUBSCRIPTION_CODE_STATUSES as readonly string[]).includes(status);

// A status this build does not know may still be billed, so the row stays on
// the page as a read-only card instead of vanishing with the customer's money.
export const isVisibleSubscriptionCode = (c: SubscriptionCode): boolean =>
  isBlockingSubscriptionCode(c) || !isKnownSubscriptionCodeStatus(c.status);

export const hasBlockingSubscriptionCodes = (
  codes: SubscriptionCode[] | undefined
): boolean => {
  const now = new Date();
  return (codes ?? []).some((c) => isBlockingSubscriptionCode(c, now));
};

// The eligibility payload names its parent plan but carries no code id, so
// match by type + name in display order; name drift (plan renamed after
// purchase) falls back to the first card of the matching type. Returning a
// single id keeps the offer box from ever rendering twice.
export const findTopupTargetCardId = (
  eligibility: TopupEligibility | undefined,
  orderedCards: CreditCardItem[]
): string | null => {
  if (!isTopupOfferReady(eligibility)) return null;
  const candidates = orderedCards.flatMap((card) =>
    card.type === 'pack' && eligibility.source_type === 'PACK'
      ? [{ id: card.code.class_pack_code_id, name: card.code.class_pack.name }]
      : card.type === 'subscription' &&
          eligibility.source_type === 'SUBSCRIPTION'
        ? [
            {
              id: card.code.subscription_code_id,
              name: card.code.subscription.name,
            },
          ]
        : []
  );
  const named = candidates.find((c) => c.name === eligibility.source_plan_name);
  return (named ?? candidates[0])?.id ?? null;
};

export const getToppedUpCreditsRow = ({
  totalLeft,
  neverExpires,
}: {
  totalLeft: number;
  neverExpires: boolean;
}): { value: string; subValue?: string } | null => {
  if (totalLeft === 0) return null;
  return {
    value: `+${totalLeft}`,
    subValue: neverExpires ? 'Never expires' : undefined,
  };
};

// Trusts status over expires_at — backend owns the ACTIVE -> EXPIRED
// transition, matching how pack codes are counted.
export const sumActiveTopupCreditsBySource = (
  topupCodes: TopupCode[] | undefined
): { packTopupCredits: number; subscriptionTopupCredits: number } => {
  let packTopupCredits = 0;
  let subscriptionTopupCredits = 0;
  for (const code of topupCodes ?? []) {
    if (code.status !== 'ACTIVE') continue;
    if (code.source_type === 'PACK') {
      packTopupCredits += getAvailableCredits(code);
    } else {
      subscriptionTopupCredits += getAvailableCredits(code);
    }
  }
  return { packTopupCredits, subscriptionTopupCredits };
};

export const sumActiveTopupCreditsForPlan = (
  topupCodes: TopupCode[] | undefined,
  parentType: 'PACK' | 'SUBSCRIPTION',
  parentCodeId: string | undefined
): { credits: number; held: number; neverExpires: boolean } => {
  const active = (topupCodes ?? []).filter((tc) => {
    if (tc.status !== 'ACTIVE' || tc.source_type !== parentType) return false;
    const tcParentId =
      parentType === 'PACK'
        ? tc.source_class_pack_code?.class_pack_code_id
        : tc.source_subscription_code?.subscription_code_id;
    return !!tcParentId && tcParentId === parentCodeId;
  });
  return {
    credits: active.reduce((sum, tc) => sum + getAvailableCredits(tc), 0),
    held: active.reduce((sum, tc) => sum + getHeldCredits(tc), 0),
    neverExpires:
      active.length > 0 && active.every((tc) => tc.expires_at === null),
  };
};

// A spent (FULLY_USED) pack earns its place on the credits surfaces only when it
// can still be topped up or already holds top-up credits; otherwise it's dead
// weight to hide, letting the buy prompt stand in. A top-up credit held for a
// waitlist is still hers, so it keeps the pack on screen too.
export const isDrainedPackHidden = (
  pack: ClassPackCode,
  isTopupTarget: boolean,
  topupCodes: TopupCode[] | undefined
): boolean => {
  if (pack.status !== 'FULLY_USED' || isTopupTarget) return false;
  const { credits, held } = sumActiveTopupCreditsForPlan(
    topupCodes,
    'PACK',
    pack.class_pack_code_id
  );
  return credits + held === 0;
};

// What the pack card's price row says. The order item is the truth (what the
// customer paid), not the catalog price, which is null or 0 for an
// admin-granted pack. A redeemed gift card's pack sits on a free order too, so
// its flag is read first: it was paid for, by someone else.
export const getPackPriceRow = (
  code: Pick<ClassPackCode, 'from_gift_card' | 'class_pack_order_item'>
): { label: string; value: string } => {
  if (code.from_gift_card) return { label: 'Price', value: 'Gift card' };
  const orderItem = code.class_pack_order_item;
  return orderItem && orderItem.discounted_price > 0
    ? {
        label: 'Paid',
        value: formatPrice(orderItem.discounted_price, orderItem.currency),
      }
    : { label: 'Price', value: 'Free' };
};

export const formatValidityPeriod = (
  days: number | null,
  options?: { short?: boolean }
): string => (days ? formatDays(days, options) : '-');

export const isNoCreditReturned = (
  isTopupBooking: boolean,
  isTopupRefundable: boolean,
  isCreditSourceActive: boolean
): boolean => (isTopupBooking ? !isTopupRefundable : !isCreditSourceActive);

export const getNoCreditReturnedReason = (
  isTopupBooking: boolean,
  isFromPack: boolean
): string =>
  isTopupBooking
    ? 'Your top-up credits have expired.'
    : isFromPack
      ? 'Your class pack has expired.'
      : 'Your membership period has ended.';

// A refund of a card that no longer works lands where nobody can spend it, so
// it is never promised or reported as money back (D135).
const GIFT_CARD_NOT_REFUNDABLE =
  "What you paid by gift card can't be refunded, because the gift card no longer works.";

// The credit goes back to the plan itself: an unlimited plan spent none, and a
// top-up booking returns to the top-up balance instead.
export const isCreditReturnedOnCancel = ({
  isCreditCodeApplied,
  isTopupBooking,
  isCreditSourceActive,
  isFromPack,
  subscriptionClassesLeft,
}: {
  isCreditCodeApplied: boolean;
  isTopupBooking: boolean;
  isCreditSourceActive: boolean;
  isFromPack: boolean;
  subscriptionClassesLeft: number | null;
}): boolean =>
  isCreditCodeApplied &&
  !isTopupBooking &&
  isCreditSourceActive &&
  (isFromPack || subscriptionClassesLeft !== null);

export const getCancelClassBodyCopy = (params: {
  k: number;
  isCreditCodeApplied: boolean;
  isFromPack: boolean;
  isCreditSourceActive: boolean;
  isTopupBooking: boolean;
  isTopupRefundable: boolean;
  classPackClassesLeft: number | null;
  subscriptionClassesLeft: number | null;
  topupCredits: number;
  isFreeBooking: boolean;
  // What was left to pay after a money gift card, null when none paid.
  giftCardRemainder: number | null;
  giftCardRefundsToCard: boolean;
}): string => {
  const {
    k,
    isCreditCodeApplied,
    isFromPack,
    isTopupBooking,
    isTopupRefundable,
    subscriptionClassesLeft,
    isFreeBooking,
    giftCardRemainder,
    giftCardRefundsToCard,
  } = params;
  const creditLabel = `${k} ${pluralizeCredits(k)}`;
  const isUnlimitedSubscription =
    !isFromPack && subscriptionClassesLeft === null;

  // Credit-redeemed bookings have final_amount === 0, so they also satisfy
  // isFreeBooking. Check credit first to show the refund-to-credit copy.
  if (isCreditCodeApplied) {
    if (isUnlimitedSubscription) {
      return "You're on an unlimited plan, so no credits were used.";
    }
    if (isTopupBooking) {
      // Cancelled seats go back to the top-up pool, not the plan, and only
      // while the top-up itself is unexpired. The plan's status is irrelevant.
      if (isTopupRefundable) {
        return `${creditLabel} will be returned to your top-up balance.`;
      }
      return `No credit will be returned. ${getNoCreditReturnedReason(isTopupBooking, isFromPack)}`;
    }
    if (isCreditReturnedOnCancel(params)) {
      return `${creditLabel} will be returned to your ${isFromPack ? 'pack' : 'membership'}.`;
    }
    return `No credit will be returned. ${getNoCreditReturnedReason(isTopupBooking, isFromPack)}`;
  }
  if (isFreeBooking) {
    return 'This booking was free, so there is nothing to refund.';
  }
  if (giftCardRemainder !== null && !giftCardRefundsToCard) {
    return giftCardRemainder === 0
      ? GIFT_CARD_NOT_REFUNDABLE
      : `${GIFT_CARD_NOT_REFUNDABLE} Any refund due on the rest will go back to your original payment method.`;
  }
  if (giftCardRemainder === 0) {
    return 'Any refund goes back to your gift card.';
  }
  if (giftCardRemainder !== null) {
    return "The gift card's share of any refund goes back to your gift card, and the rest to your original payment method.";
  }
  return 'Any refund due will go back to your original payment method.';
};

// What the cancel reply says came back, each part as the server reported it:
// a money gift card's share to the card and the rest through Stripe. The
// backend reports 0 for the rest both when no refund was due and when the
// Stripe refund threw, so a rest that came back as nothing is left with the
// studio rather than left out, since the modal promised it (D255).
export const getCancelRefundCopy = (
  refundedAmount: number,
  giftCardRefundedAmount: number,
  currency: string,
  giftCardRefundsToCard: boolean,
  // What was left to pay after a money gift card, null when none paid.
  giftCardRemainder: number | null
): string => {
  const parts = [
    giftCardRefundedAmount > 0 &&
      `${formatAmount(giftCardRefundedAmount, currency)} is back on your gift card.`,
    refundedAmount > 0 &&
      `${formatAmount(refundedAmount, currency)} was refunded to your original payment method. Allow 5–10 business days for it to appear.`,
  ].filter(Boolean);
  const restWithStudio =
    (giftCardRemainder ?? 0) > 0 &&
    refundedAmount === 0 &&
    'If a refund is due on the rest, the studio will process it.';
  if (!giftCardRefundsToCard) {
    return [
      ...(parts.length ? parts : ['Your cancellation is recorded.']),
      GIFT_CARD_NOT_REFUNDABLE,
      restWithStudio,
    ]
      .filter(Boolean)
      .join(' ');
  }
  return parts.length
    ? [...parts, restWithStudio].filter(Boolean).join(' ')
    : 'Your cancellation is recorded. If a refund is due, it will be processed by the studio.';
};
